/**
 * Progresso da importação guardado no navegador.
 *
 * O lote em si já vive no servidor: o preview fica gravado em `ImportBatch` e
 * o modal o retoma sozinho ao reabrir. O que se perdia era tudo que o usuário
 * decidiu em cima dele — conciliação de grupos e categorias, duplicatas
 * revisadas e as correções digitadas nas linhas com problema.
 * Fechar o modal sem querer jogava fora um trabalho que, numa planilha de
 * produção, leva vários minutos.
 *
 * Fica no navegador de propósito: é estado de tela, e o dado de verdade já
 * está persistido do outro lado. A chave é o id do lote, então dois lotes não
 * se misturam e nada vaza de um arquivo para outro.
 */

const PREFIXO = 'ltq:import-progresso:';

function chave(batchId: string, escopo: string): string {
    return `${PREFIXO}${batchId}:${escopo}`;
}

/**
 * localStorage lança em aba anônima e com cookies bloqueados, e o conteúdo
 * pode estar corrompido por uma versão anterior do app. Nenhum dos dois pode
 * derrubar a importação: na dúvida, começa do zero.
 */
export function carregarProgresso<T>(batchId: string, escopo: string): T | null {
    try {
        const bruto = window.localStorage.getItem(chave(batchId, escopo));
        return bruto ? (JSON.parse(bruto) as T) : null;
    } catch {
        return null;
    }
}

export function salvarProgresso<T>(batchId: string, escopo: string, valor: T): void {
    try {
        window.localStorage.setItem(chave(batchId, escopo), JSON.stringify(valor));
    } catch {
        // Sem espaço ou sem permissão: o usuário perde só a retomada.
    }
}

export function limparProgresso(batchId: string): void {
    try {
        const aRemover: string[] = [];
        for (let i = 0; i < window.localStorage.length; i += 1) {
            const k = window.localStorage.key(i);
            if (k?.startsWith(`${PREFIXO}${batchId}:`)) aRemover.push(k);
        }
        for (const k of aRemover) window.localStorage.removeItem(k);
    } catch {
        // idem
    }
}

/**
 * Remove progresso de lotes que não estão mais em jogo. Sem isso o navegador
 * acumularia uma entrada por importação para sempre.
 */
export function limparProgressoAntigo(batchIdAtual: string | null): void {
    try {
        const aRemover: string[] = [];
        for (let i = 0; i < window.localStorage.length; i += 1) {
            const k = window.localStorage.key(i);
            if (!k?.startsWith(PREFIXO)) continue;
            if (batchIdAtual && k.startsWith(`${PREFIXO}${batchIdAtual}:`)) continue;
            aRemover.push(k);
        }
        for (const k of aRemover) window.localStorage.removeItem(k);
    } catch {
        // idem
    }
}
